/**
 * Which way to send light from a point: the textbook's construction rays for a
 * lens or mirror, or a fan across its aperture.
 *
 * Construction rays are CHOSEN rays — parallel to the axis, through the centre,
 * through the focus, through the centre of curvature — picked because the book
 * can say where each goes. They are traced like any other ray; nothing here
 * decides where they end up.
 */
import { defOf } from './registry';
import { traceRay } from './trace';
import { ConstructionRule, Surface, ThinElement, Vec2, World } from './types';
import { add, dist, dot, len, neg, perp, scale, sub, unit } from './vec';

export const surfacesOf = (w: World): Surface[] => w.parts.flatMap(p => defOf(p).surfaces(p, w));

export const elementsOf = (w: World): ThinElement[] =>
    w.parts.flatMap(p => {
        const k = defOf(p);
        return k.element ? [k.element(p, w)] : [];
    });

/** The element's axis, turned to point the way light from `from` travels towards it. */
export const towards = (el: ThinElement, from: Vec2): Vec2 =>
    dot(sub(el.centre, from), el.axis) >= 0 ? el.axis : neg(el.axis);

/**
 * The lens or mirror light from `from` meets first. `prefer` — the selected
 * element — wins when it can be reached at all. Elements downstream of the
 * light (to the right) are preferred, as light on this bench travels that way
 * by convention.
 */
export function targetFor(w: World, surfaces: Surface[], from: Vec2, prefer?: string): ThinElement | null {
    const reachable = elementsOf(w).filter(el => {
        // A mirror seen from behind is still aimed at: the rays are drawn to its
        // hatched back and stop there, which is what the student has to see.
        if (dist(el.centre, from) < 1e-9) return false;
        const probe = traceRay(surfaces, from, sub(el.centre, from), { probe: true });
        return probe.vertices[1]?.partId === el.partId;
    });
    const preferred = reachable.find(el => el.partId === prefer);
    if (preferred) return preferred;
    const ahead = reachable.filter(el => el.centre.x > from.x);
    const pool = ahead.length ? ahead : reachable;
    pool.sort((a, b) => dist(a.centre, from) - dist(b.centre, from));
    return pool[0] ?? null;
}

export interface Aim { direction: Vec2; rule: ConstructionRule }

/** The textbook's rays from `from` for this element. */
export function constructionAims(from: Vec2, el: ThinElement): Aim[] {
    const a = towards(el, from);
    const aims: Aim[] = [];
    const add1 = (direction: Vec2, rule: ConstructionRule): void => {
        if (dot(direction, a) < 1e-6) return;
        if (aims.some(x => len(sub(x.direction, direction)) < 1e-9)) return;
        aims.push({ direction, rule });
    };
    /** Along the line through X, heading towards the element. */
    const through = (X: Vec2, rule: ConstructionRule): void => {
        const d = sub(X, from);
        if (len(d) < 1e-9) return;
        const u = unit(d);
        add1(dot(u, a) < 0 ? neg(u) : u, rule);
    };

    add1(a, 'parallel');
    through(el.centre, el.folds ? 'pole' : 'centre');
    if (Number.isFinite(el.f)) {
        // The focal point on the incoming side for a converging element, the far
        // one for a diverging element — the same line either way: F = centre − f·a.
        through(sub(el.centre, scale(a, el.f)), 'focus');
        if (el.folds) through(sub(el.centre, scale(a, 2 * el.f)), 'curvature');
    }
    return aims;
}

/** `count` rays from `from`, spread evenly across the element's aperture. */
export function fanAims(from: Vec2, el: ThinElement, count: number, inset = 0.95): Vec2[] {
    const a = towards(el, from);
    const n = perp(el.axis);
    const h = el.halfAperture * inset;
    const out: Vec2[] = [];
    for (let i = 0; i < count; i++) {
        const target = add(el.centre, scale(n, count === 1 ? 0 : -h + (2 * h * i) / (count - 1)));
        const d = unit(sub(target, from));
        if (dot(d, a) > 1e-6) out.push(d);
    }
    return out;
}
